import { useTranslation } from 'react-i18next';
import { useTaskStatuses } from '../hooks/useTaskStatuses';
import type { TicketStatus, MilestoneStatus, ProjectStatus } from '../types';
import Badge from './ui/Badge';
import { statusTone } from './ui/tone';

type StatusType = TicketStatus | MilestoneStatus | ProjectStatus;

interface StatusBadgeProps {
  status: StatusType;
  className?: string;
  /** D-146 — the row's task_type: a service request ('ticket') reads "בטיפול" / "הושלמה". */
  taskType?: string | null;
}

export default function StatusBadge({ status, className = '', taskType }: StatusBadgeProps) {
  const { t } = useTranslation();

  // Unified i18n: only project-specific statuses use projectStatus prefix.
  // All ticket/milestone statuses (including legacy open/waiting/closed/pending) use milestoneStatus.
  const projectOnly = ['active', 'on_hold', 'cancelled'];
  const labelKey = projectOnly.includes(status) ? `projectStatus.${status}` : `milestoneStatus.${status}`;

  // B.13 — task-status labels come from the controlled task_statuses list.
  const { labelOf } = useTaskStatuses();
  const label = (!projectOnly.includes(status) && labelOf(status, taskType)) || t(labelKey);

  // Colour comes from the single status → tone map (DESIGN_SYSTEM.md §2.4).
  return (
    <Badge tone={statusTone(status)} dot className={className}>
      {label}
    </Badge>
  );
}
